import { Component, inject, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from "@angular/material/icon";
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { differenceInMinutes, format } from 'date-fns';
import { LatLng } from 'leaflet';
import { firstValueFrom } from 'rxjs';
import { DialogsService } from '../dialogs.service';
import { DisplayWorkDayEventType, NextServicio, PauseReason, WorkDayEvent, WorkDayEventType } from '../../modules/admin/services/services.types';
import { DTypeService } from '../../core/dtype/dtype.service';
import { WorkdayGeopositionMarkerData, WorkdayReferenceGeopositionMarkerData } from '../../modules/admin/tasks/tasks.types';
import { MasterLocationCiCoBaseColumns } from '../../core/master-data/master-data.types';

@Component({
    selector: 'app-workday-event-list-dialog',
    templateUrl: './workday-event-list-dialog.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [TranslocoModule, MatIcon, MatTooltipModule, MatButtonModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        class: 'flex-1'
    }
})
export class WorkdayEventListDialogComponent implements OnInit {

    public data: { workdayEventList: WorkDayEvent[], service: NextServicio } = inject(MAT_DIALOG_DATA);

    workdayEventList = this.data.workdayEventList.map(event => ({
        ...event,
        hasGeoposition: event.latitude && event.longitude,
        formattedTime: format(event.queueTimestamp, 'HH:mm')
    }));

    displayWorkdayEventList: any[] = [];

    private dtypeService = inject(DTypeService);
    private dialogRef = inject(MatDialogRef<WorkdayEventListDialogComponent>);
    private dialogsService = inject(DialogsService);
    private translocoService = inject(TranslocoService);

    pauseReasonNames = new Map();

    DisplayWorkDayEventType = DisplayWorkDayEventType;
    PauseReason = PauseReason;

    ngOnInit(): void {
        this.displayWorkdayEventList = [];
        const workdayEventList = this.workdayEventList;

        for (let i = 0; i < workdayEventList.length; i++) {
            const current = workdayEventList[i];
            const next = workdayEventList[i + 1];

            if (current.type === WorkDayEventType.PAUSE_WORKDAY && next?.type === WorkDayEventType.RESTART_WORKDAY) {
                this.displayWorkdayEventList.push({
                    type: 'PAUSE_RESTART',
                    pause: current,
                    restart: next,
                    difference: Math.abs(differenceInMinutes(current.queueTimestamp, next.queueTimestamp)),
                    queueTimestamp: current.queueTimestamp
                });
                i++;
            } else {
                this.displayWorkdayEventList.push(current);
            }
        }

        this.pauseReasonNames.set(PauseReason.BREAKFAST, this.data.service?.breakfastName || this.translocoService.translate("pauseReason.BREAKFAST"));
        this.pauseReasonNames.set(PauseReason.LUNCH, this.data.service?.lunchName || this.translocoService.translate("pauseReason.LUNCH"));
        this.pauseReasonNames.set(PauseReason.ANY, this.translocoService.translate("pauseReason.ANY"));
    }

    async openWorkdayGeopisition(workdayEvent?: WorkDayEvent) {
        const workDayEventList = this.workdayEventList;
        const ciCoEvents = workDayEventList.filter(w => 
            w.type === WorkDayEventType.START_WORKDAY || 
            w.type === WorkDayEventType.END_WORKDAY
        );

        const geopositionSummaryList: WorkdayGeopositionMarkerData[] = ciCoEvents
            .map(w => {
                return {
                    event: w.type,
                    geopositionTimestamp: w.geopositionTimestamp,
                    latitude: w.latitude,
                    longitude: w.longitude,
                }
            }) ?? [];

        const referenceGeopositionSummaryList: WorkdayReferenceGeopositionMarkerData[] = await Promise.all(ciCoEvents
            .map(async w => {
                if (!w.addressCiCoOid || !w.addressCiCoUri) {
                    return null;
                }
                const masterLocationCiCo: MasterLocationCiCoBaseColumns = await firstValueFrom(this.dtypeService.retrieveDtypeDataItemByUriAndOid(w.addressCiCoUri, w.addressCiCoOid))
                    .catch(error => {
                        console.error(error);
                        return null;
                    });
                return masterLocationCiCo ? {
                    latitude: masterLocationCiCo.address?.latitude,
                    longitude: masterLocationCiCo.address?.longitude,
                    name: masterLocationCiCo.name
                } : null;
            })) ?? [];

        let initialMapCenterPosition: LatLng | undefined = undefined;
        if (workdayEvent?.latitude !== undefined && workdayEvent?.longitude !== undefined) {
            initialMapCenterPosition = new LatLng(workdayEvent?.latitude, workdayEvent?.longitude);
        }

        return this.dialogsService.openGeopositionDialog(geopositionSummaryList, referenceGeopositionSummaryList, initialMapCenterPosition);
    }

    async closeDialog() {
        this.dialogRef.close();
    }
}
